import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface TablePagerProps {
  page: number;
  onPageChange: (page: number) => void;
  /** Total items — omit for hasMore mode */
  total?: number | null;
  pageSize: number;
  /** Used when total is unknown */
  hasMore?: boolean;
}

export function TablePager({ page, onPageChange, total, pageSize, hasMore }: TablePagerProps) {
  const knownTotal = total != null && total > 0;
  const totalPages = knownTotal ? Math.ceil(total / pageSize) : undefined;

  const start = (page - 1) * pageSize + 1;
  const end = knownTotal ? Math.min(page * pageSize, total) : page * pageSize;

  const canPrev = page > 1;
  const canNext = totalPages ? page < totalPages : (hasMore ?? false);

  // Build page number buttons (only when total is known)
  const pageButtons: (number | '...')[] = [];
  if (totalPages && totalPages > 1) {
    const maxButtons = 11;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pageButtons.push(i);
    } else {
      pageButtons.push(1);
      if (page > 5) pageButtons.push('...');
      const rangeStart = Math.max(2, page - 3);
      const rangeEnd = Math.min(totalPages - 1, page + 3);
      for (let i = rangeStart; i <= rangeEnd; i++) pageButtons.push(i);
      if (page < totalPages - 4) pageButtons.push('...');
      pageButtons.push(totalPages);
    }
  }

  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-sm font-bold text-muted-foreground">
        {knownTotal
          ? `Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()}`
          : `Showing ${start.toLocaleString()}-${end.toLocaleString()}`}
      </p>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" onClick={() => onPageChange(page - 1)} disabled={!canPrev}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {pageButtons.map((p, i) =>
          p === '...' ? (
            <span key={`dots-${i}`} className="px-2 text-sm text-muted-foreground">...</span>
          ) : (
            <Button
              key={p}
              size="sm"
              variant={p === page ? 'default' : 'outline'}
              className="min-w-[2rem]"
              onClick={() => onPageChange(p)}
            >
              {p.toLocaleString()}
            </Button>
          )
        )}
        <Button size="sm" variant="outline" onClick={() => onPageChange(page + 1)} disabled={!canNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
